import { z } from "zod";
import { GENDER_VALUES } from "@typings/entities/individuals";
import { SAMPLE_TYPES } from "@typings/entities/samples/enums";
import { validateLatitude, validateLongitude } from "@utils/formatters";
import { config } from "@/config";
import { ComboBoxDefault } from "@typings/components/comboBox";
import type { DefaultValues } from "react-hook-form";

export const sampleSchema = z
  .object({
    reporter: z.object({
      label: z.string().min(1, "Agente designado é obrigatório"),
      value: z.string().min(1, "Agente designado é obrigatório"),
    }),
    visit: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .nullable()
      .optional(),
    individual: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .nullable()
      .optional(),
    anonymousIndividualTaxonomicGroup: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .nullable()
      .optional(),
    anonymousIndividualGender: z
      .enum(GENDER_VALUES, {
        error: "Gênero deve ser um valor válido",
      })
      .optional(),
    latitude: z
      .string({ error: "Latitude é obrigatória" })
      .refine(validateLatitude(), {
        message: "Latitude deve estar entre -90 e 90",
      }),
    longitude: z
      .string({ error: "Longitude é obrigatória" })
      .refine(validateLongitude(), {
        message: "Longitude deve estar entre -180 e 180",
      }),
    type: z.enum(SAMPLE_TYPES, {
      error: "Tipo de registro deve ser um valor válido",
    }),
    sampleDate: z.string().optional(),
    destination: z
      .string({ error: "Destino é obrigatório" })
      .min(1, "Destino é obrigatório"),
    observations: z.string().optional(),
    attachments: z
      .array(z.instanceof(File))
      .max(
        config.uploadArchive.maxFiles,
        `Você pode enviar no máximo ${config.uploadArchive.maxFiles} arquivos.`,
      )
      .optional(),
  })
  .refine(
    (data) => {
      const hasIndividual = !!data.individual?.value;
      const hasAnonymous = !!(
        data.anonymousIndividualTaxonomicGroup?.value ||
        data.anonymousIndividualGender
      );

      return !(hasIndividual && hasAnonymous);
    },
    {
      message:
        "Não é possível preencher tanto o indivíduo quanto os dados anônimos ao mesmo tempo",
      path: ["individual"],
    },
  );

export type SampleFormData = z.infer<typeof sampleSchema>;

export const sampleDefaultValues: DefaultValues<SampleFormData> = {
  reporter: ComboBoxDefault,
  visit: ComboBoxDefault,
  individual: ComboBoxDefault,
  anonymousIndividualTaxonomicGroup: ComboBoxDefault,
  anonymousIndividualGender: undefined,
  latitude: undefined,
  longitude: undefined,
  type: undefined,
  sampleDate: undefined,
  destination: "",
  observations: "",
};
